/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Widget Multi Balances Component
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.11.0
 * @since 1.10.0
 */

import { Fragment, useEffect } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { EllipseText, LinkBlock } from '../../../../../../components/common';
import initTranslationCommon from '../../../../../../modules/internationalization/constants/common';
import initTranslationPageDashboard
    from '../../../../../../modules/internationalization/constants/pages/dashboard/dashboard';
import ICommonTranslation from '../../../../../../modules/internationalization/types/common';
import IPageDashboardTranslation from '../../../../../../modules/internationalization/types/pages/dashboard/dashboard';
import { makeSelectLocalStorageSelectedAccount } from '../../../../../../modules/storage/storageSelectors';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import { CLIENT_ID, RoutePath } from '../../../../../../routes/constants/routesConstants';
import { formatBalance } from '../../../../../../utils/formatText';
import { WidgetTitleStyled } from '../../styled/WidgetStyled';
import { IWidgetSearchAccountsItem } from '../../WidgetSearchAccounts/types/widgetSearchAccountsTypes';
import { widgetMultiBalancesGetListAction } from '../widgetMultiBalancesActions';
import { makeSelectWidgetMultiBalancesList } from '../widgetMultiBalancesSelectors';
import { IMultiBalance } from '../widgetMultiBalancesTypes';
import {
    WidgetMultiBalancesItemStyled,
    WidgetMultiBalancesListStyled,
    WidgetMultiBalancesStyled,
} from './styled/WidgetMultiBalancesStyled';

interface IWidgetMultiBalancesProps {
    translation: IPageDashboardTranslation & ICommonTranslation;
}

export type TWidgetMultiBalancesProps = IConnectedProps & IDispatchProps & IWidgetMultiBalancesProps;

export const WidgetMultiBalances = (props: TWidgetMultiBalancesProps) => {
    const {
        multiBalances,
        multiBalancesGetList,
        selectedAccount,
        translation,
    } = props;

    useEffect(() => {
        multiBalancesGetList();
    }, [multiBalancesGetList]);

    if (!multiBalances.length) {
        return null;
    }

    return (
        <WidgetMultiBalancesStyled>
            <WidgetTitleStyled>
                {translation.widgets.multiBalances.title}
            </WidgetTitleStyled>
            <WidgetMultiBalancesListStyled>
                {
                    multiBalances.map((item: IMultiBalance) => <Fragment key={item.name}>
                        <WidgetMultiBalancesItemStyled>
                            <div className="widget-multi-balances__name">
                                <EllipseText title={item.name}>{item.name}</EllipseText>
                            </div>
                            <div>{formatBalance(item.remaining)} {translation.currency}</div>
                        </WidgetMultiBalancesItemStyled>
                    </Fragment>)
                }
            </WidgetMultiBalancesListStyled>
            <LinkBlock
                url={`${RoutePath.accountInfoFinancialMultiBalances
                    .replace(CLIENT_ID, selectedAccount.id.toString())}`}>
                {translation.widgets.multiBalances.moreLink}
            </LinkBlock>
        </WidgetMultiBalancesStyled>
    );
};

WidgetMultiBalances.defaultProps = {
    translation: { ...initTranslationPageDashboard, ...initTranslationCommon },
};

interface IConnectedProps {
    multiBalances: IMultiBalance[];
    selectedAccount: IWidgetSearchAccountsItem;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    multiBalances: makeSelectWidgetMultiBalancesList(),
    selectedAccount: makeSelectLocalStorageSelectedAccount(),
});

interface IDispatchProps {
    multiBalancesGetList: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    multiBalancesGetList: () => dispatch(widgetMultiBalancesGetListAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(WidgetMultiBalances);
